import get from 'lodash/get';
import set from 'lodash/set';
import type { DigitalDataPathPatch } from '../types/analytics';
import type { BaselineDigitalData } from '../types/digital-data';
import { isForbiddenPatchPath } from './digital-data-sanitizer';

const FORBIDDEN_PATH_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);

export class ForbiddenPathPatchError extends Error {
  constructor(path: string) {
    super(`Forbidden path segment in patch: ${path}`);
    this.name = 'ForbiddenPathPatchError';
  }
}

export type SanitizedPathPatchResult = {
  sanitized: DigitalDataPathPatch;
  strippedPaths: string[];
};

function assertPathAllowed(path: string): void {
  const segments = path.split(/[.[\]]+/).filter(Boolean);
  if (segments.some((segment) => FORBIDDEN_PATH_SEGMENTS.has(segment))) {
    throw new ForbiddenPathPatchError(path);
  }
}

function applyMetricPath(target: BaselineDigitalData, path: string, value: unknown): void {
  assertPathAllowed(path);
  if (Array.isArray(value)) {
    const existing = get(target, path);
    set(target, path, Array.isArray(existing) ? [...existing, ...value] : value);
    return;
  }
  set(target, path, value);
}

export function sanitizePathPatch(pathPatch: DigitalDataPathPatch): SanitizedPathPatchResult {
  const strippedPaths: string[] = [];

  const sanitized: DigitalDataPathPatch = {};
  for (const [path, value] of Object.entries(pathPatch)) {
    if (isForbiddenPatchPath(path)) {
      strippedPaths.push(path);
    } else {
      sanitized[path] = value;
    }
  }
  return { sanitized, strippedPaths };
}

export function applyPathPatch(
  target: BaselineDigitalData,
  pathPatch: DigitalDataPathPatch
): string[] {
  const { sanitized, strippedPaths } = sanitizePathPatch(pathPatch);

  for (const [path, value] of Object.entries(sanitized)) {
    applyMetricPath(target, path, value);
  }

  return strippedPaths;
}
